/**
 * RTK Query Base API Configuration — Centralized API Layer
 * 
 * SECURITY:
 * - 401 Unauthorized → auto logout + redirect to landing page
 * - credentials: 'include' → cookies sent with every request
 * 
 * CACHING:
 * - keepUnusedDataFor: 60 → Keep cache for 60 seconds (prevents blank screen on refresh)
 * - refetchOnFocus: true → Silently refresh when user returns to tab
 * - refetchOnReconnect: true → Silently refresh when network reconnects
 * 
 * ALL modules should use baseApi.injectEndpoints() for their API slices.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { freshReplayHeaders } from '../../app/shared/services/replayHeaders';
import { clientBindingHeaders } from '../../app/shared/services/clientBinding';

// Use same BASE_URL logic as existing apiClient
const BASE_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

/**
 * Base fetchBaseQuery with cookie credentials
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: 'include', // Send cookies with every request
  // Replay protection (security audit #14): every mutation carries a fresh
  // nonce + timestamp. Queries (GETs) are not guarded and stay header-free.
  // prepareHeaders runs per attempt, so an RTK retry gets a new nonce.
  prepareHeaders: (headers, { type }) => {
    // Client binding (security audit #14) goes on every request, queries
    // included: the server checks it wherever it checks the session.
    Object.entries(clientBindingHeaders()).forEach(([name, value]) => headers.set(name, value));
    if (type === 'mutation') {
      Object.entries(freshReplayHeaders()).forEach(([name, value]) => headers.set(name, value));
    }
    return headers;
  },
});

/**
 * Wrapper around rawBaseQuery that handles 401 Unauthorized responses.
 * On 401 → clears auth data, resets API cache, redirects to landing page.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // --- Session expired / unauthorized ---
    console.warn('[Auth] 401 Unauthorized — session expired. Logging out...');

    // 1. Clear all auth data from localStorage
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('captchaId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoleCategory');
    localStorage.removeItem('userRoleId');
    localStorage.removeItem('username');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');

    // 2. Clear sessionStorage (cached dashboard/attendance data)
    sessionStorage.clear();

    // 3. Reset RTK Query cache to prevent stale authenticated data
    api.dispatch(baseApi.util.resetApiState());

    // 4. Dispatch Redux logout action
    api.dispatch({ type: 'auth/logout' });

    // 5. Redirect to landing page for re-login
    //    Using window.location ensures full page reload (clears React state)
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  
  // Global settings - keep cache for 5 minutes to reduce API calls
  // Static data endpoints can override with longer keepUnusedDataFor (e.g., 3600s)
  // Components can manually refetch when needed using refetch() from hooks
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: false,

  // Tag types for cache invalidation — all modules register their tags here
  tagTypes: [
    'Dashboard',
    'Attendance',
    'Employee',
    'Student',
    'KVS',
    'ActivityTree',
    'Expense',
    'Financial',
    'Samagam',
    'SamagamInfra',
    'SamagamEnrollment',
    'SamagamICT',
    'SamagamLibrary',
    'SamagamCWSN',
    'SamagamNCC',
    'Admission',
  ],

  // Endpoints will be injected from separate module files via baseApi.injectEndpoints()
  endpoints: () => ({}),
});
